import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { isNative } from '../utils/native';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Lock, Loader2, Eye, EyeOff, ShieldCheck, ArrowRight } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const rules = [
  { id: 'len', label: 'At least 8 characters', test: (p) => p.length >= 8 },
  { id: 'upper', label: 'One uppercase letter', test: (p) => /[A-Z]/.test(p) },
  { id: 'lower', label: 'One lowercase letter', test: (p) => /[a-z]/.test(p) },
  { id: 'digit', label: 'One digit', test: (p) => /\d/.test(p) },
  { id: 'special', label: 'One special character', test: (p) => /[^A-Za-z0-9]/.test(p) },
];

export default function FirstLoginPasswordChange() {
  const navigate = useNavigate();
  const { token, user, setUser } = useAuth();
  const [form, setForm] = useState({ current: '', next: '', confirm: '' });
  const [showPw, setShowPw] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const allRules = rules.map(r => ({ ...r, ok: r.test(form.next) }));
  const passwordOk = allRules.every(r => r.ok);
  const matchOk = form.next.length > 0 && form.next === form.confirm;

  const submit = async (e) => {
    e.preventDefault();
    if (!form.current || !form.next) { toast.error('Please complete all fields'); return; }
    if (!passwordOk) { toast.error('Password does not meet complexity requirements'); return; }
    if (!matchOk) { toast.error('Passwords do not match'); return; }
    if (form.current === form.next) { toast.error('New password must be different from the temporary password'); return; }

    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/auth/change-password`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ current_password: form.current, new_password: form.next }),
      });
      const data = await res.json();
      if (res.ok) {
        toast.success('Password updated. Welcome aboard!');
        if (setUser && user) setUser({ ...user, require_password_change: false });
        const role = user?.role;
        if (isNative) navigate('/dashboard');
        else if (role === 'platform_owner') navigate('/platform');
        else if (role === 'agent_admin' || role === 'managing_agent' || role === 'super_agent') navigate('/agent');
        else if (role === 'trustee') navigate('/trustee-hub');
        else navigate('/dashboard');
      } else toast.error(data.detail || 'Failed to update password');
    } catch { toast.error('Network error'); }
    finally { setSubmitting(false); }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-white via-sky-50 to-emerald-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-md bg-white border-slate-200 shadow-lg" data-testid="first-login-card">
        <CardHeader>
          <div className="w-12 h-12 rounded-xl bg-indigo-100 flex items-center justify-center mb-3">
            <ShieldCheck className="w-6 h-6 text-indigo-600" />
          </div>
          <CardTitle className="text-slate-800">Set Your Password</CardTitle>
          <CardDescription>
            For security, you must set a new password before continuing. Your temporary password was sent in the welcome email.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={submit} className="space-y-4">
            <div className="space-y-2">
              <Label>Temporary Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                <Input
                  type={showPw ? 'text' : 'password'} value={form.current}
                  onChange={(e) => setForm({ ...form, current: e.target.value })}
                  className="pl-10 pr-10" placeholder="From your welcome email" autoFocus
                  data-testid="current-password-input"
                />
                <button type="button" onClick={() => setShowPw(v => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500" data-testid="toggle-show-password">
                  {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <div className="space-y-2">
              <Label>New Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                <Input
                  type={showPw ? 'text' : 'password'} value={form.next}
                  onChange={(e) => setForm({ ...form, next: e.target.value })}
                  className="pl-10" data-testid="new-password-input"
                />
              </div>
              <ul className="text-xs space-y-0.5 mt-2">
                {allRules.map(r => (
                  <li key={r.id} className={r.ok ? 'text-emerald-600' : 'text-slate-400'} data-testid={`req-${r.id}`}>
                    {r.ok ? '✓' : '○'} {r.label}
                  </li>
                ))}
              </ul>
            </div>

            <div className="space-y-2">
              <Label>Confirm New Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                <Input
                  type={showPw ? 'text' : 'password'} value={form.confirm}
                  onChange={(e) => setForm({ ...form, confirm: e.target.value })}
                  className="pl-10" data-testid="confirm-password-input"
                />
              </div>
              {form.confirm.length > 0 && (
                <p className={`text-xs ${matchOk ? 'text-emerald-600' : 'text-red-600'}`} data-testid="match-indicator">
                  {matchOk ? '✓ Passwords match' : '✗ Passwords do not match'}
                </p>
              )}
            </div>

            <Button type="submit" disabled={submitting} className="w-full bg-indigo-600 hover:bg-indigo-700 h-11" data-testid="change-password-btn">
              {submitting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Updating…</> : <>Set Password & Continue<ArrowRight className="w-4 h-4 ml-2" /></>}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
